import { z } from 'zod';
import type { FieldValues, Resolver } from 'react-hook-form';
import { formatRegistrationNumber, formatName, formatNameKeepInvalid } from './format';

/**
 * Pure validation helper for phone numbers.
 *
 * Rules (in order):
 *  - empty / whitespace-only   → "Phone number is required"
 *  - contains non-digits       → "Invalid phone number"
 *  - not exactly 10 digits      → "Phone number must be 10 digits"
 *  - otherwise                  → null (valid)
 *
 * The country dial code is derived server-side from the creator's country
 * (Country.phone_code), so callers must NOT collect a country-code prefix here.
 */
export function phoneError(value: string): string | null {
  if (!value || !value.trim()) return 'Phone number is required';
  const digits = value.replace(/\D/g, '');
  if (!/^\d+$/.test(value.trim())) return 'Invalid phone number';
  if (digits.length !== 10) return 'Phone number must be 10 digits';
  return null;
}

/**
 * True for a number made of one digit repeated ("0000000000", "9999999999").
 * Used by the insurer Team form (#361), which refuses such placeholders; it is
 * NOT part of {@link phoneError}, so no other form's rules change.
 */
export function isRepeatedDigitPhone(value: string): boolean {
  const digits = (value || '').replace(/\D/g, '');
  return digits.length > 0 && /^(\d)\1*$/.test(digits);
}

/**
 * Zod schema mirroring {@link phoneError} for forms that use zod resolvers.
 * Surfaces the same messages so client validation is consistent everywhere.
 */
export const phoneSchema = z.string().superRefine((value, ctx) => {
  const error = phoneError(value);
  if (error) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
  }
});

/**
 * Strips every non-digit character from an input as the user types. Designed to
 * be passed to react-hook-form's `register('field', { onChange: digitsOnlyOnChange })`
 * so phone / mobile fields accept digits only. Mutating `target.value` before
 * RHF reads it keeps the form state and the DOM in sync.
 */
export function digitsOnlyOnChange(e: { target: { value: string } }) {
  e.target.value = e.target.value.replace(/\D/g, '');
}

/**
 * RHF onChange transforms for BROKER-10 real-time input formatting. Mutating
 * `target.value` before react-hook-form reads it keeps form state and the DOM
 * in sync (same technique as digitsOnlyOnChange), so formatting applies live as
 * the user types AND on paste, blocking invalid characters at entry.
 * Usage: register('registration_number', { onChange: registrationOnChange }).
 */
export function registrationOnChange(e: { target: { value: string } }) {
  e.target.value = formatRegistrationNumber(e.target.value);
}

export function nameOnChange(e: { target: { value: string } }) {
  e.target.value = formatName(e.target.value);
}

/** Like nameOnChange but keeps digits/symbols in the box so the form's
 * validation (`personNameProblem`) can show a visible message (DQ-640). */
export function nameKeepOnChange(e: { target: { value: string } }) {
  e.target.value = formatNameKeepInvalid(e.target.value);
}

/**
 * Name input that never re-cases what was typed ("QA" stays "QA", "aman" stays
 * "aman", "McDonald" stays "McDonald"); only leading spaces are dropped. Digits
 * and symbols stay visible so `personNameProblem` can explain them. Used by
 * the insurer Team form (#361); the global `nameOnChange` rule is unchanged.
 */
export function nameAsTypedOnChange(e: { target: { value: string } }) {
  e.target.value = (e.target.value || '').replace(/^\s+/, '');
}

/**
 * Mirror of the backend's shared email check (`apps.core.validators.email_problem`).
 * Offline and format-only: catches a missing or numeric TLD, trailing or doubled dots,
 * spaces, and well-known mistyped provider domains. Keep both tables in step.
 */
export const EMAIL_DOMAIN_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.om': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmail.comm': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'yahoo.co.uk.com': 'yahoo.co.uk',
  'outlok.com': 'outlook.com',
  'outlook.con': 'outlook.com',
};

const EMAIL_LOCAL = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+$/;
const EMAIL_LABEL = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
const EMAIL_TLD = /^([a-z]{2,63}|xn--[a-z0-9-]{2,59})$/;

/** Why this address is wrong, or null when it looks fine (blank is fine). */
export function emailError(value: string | null | undefined): string | null {
  const email = (value ?? '').trim().toLowerCase();
  if (!email) return null;
  const at = email.lastIndexOf('@');
  const local = at > 0 ? email.slice(0, at) : '';
  const domain = at >= 0 ? email.slice(at + 1) : '';
  if (!local || !domain || /\s/.test(email) || email.includes('..')) return 'Invalid email';
  if (local.startsWith('.') || local.endsWith('.') || !EMAIL_LOCAL.test(local)) return 'Invalid email';
  const labels = domain.split('.');
  if (labels.length < 2 || !labels.every((label) => EMAIL_LABEL.test(label))) return 'Invalid email';
  if (!EMAIL_TLD.test(labels[labels.length - 1])) return 'Invalid email';
  const fixed = EMAIL_DOMAIN_TYPOS[domain];
  return fixed ? `Did you mean ${local}@${fixed}?` : null;
}

/** Optional email field: blank is allowed, anything else must pass {@link emailError}. */
export const optionalEmailSchema = z
  .string()
  .superRefine((value, ctx) => {
    const error = emailError(value);
    if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
  })
  .optional();

/** Required email field: not blank, and passes {@link emailError}. */
export const requiredEmailSchema = z
  .string()
  .min(1, 'Email is required')
  .superRefine((value, ctx) => {
    const error = emailError(value);
    if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
  });

/**
 * Wrap a react-hook-form resolver so an email that is unchanged from what is already
 * stored is not re-checked -- the backend does the same, so a record with an old bad
 * address can still be edited for other fields.
 */
export function allowUnchangedEmails<T extends FieldValues>(
  resolver: Resolver<T>,
  originals: () => Partial<Record<string, string | null | undefined>>,
  fields: string[] = ['email'],
): Resolver<T> {
  const norm = (v: unknown) => String(v ?? '').trim().toLowerCase();
  return async (values, context, options) => {
    const stored = originals();
    const probe: Record<string, unknown> = { ...values };
    const unchanged = fields.filter((f) => norm(values[f]) && norm(values[f]) === norm(stored[f]));
    unchanged.forEach((f) => {
      probe[f] = '';
    });
    const result = await resolver(probe as T, context, options);
    if (unchanged.length && Object.keys(result.errors).length === 0) {
      const restored: Record<string, unknown> = { ...(result.values as object) };
      unchanged.forEach((f) => {
        restored[f] = values[f];
      });
      return { ...result, values: restored as T } as ReturnType<Resolver<T>> extends Promise<infer R> ? R : never;
    }
    return result;
  };
}
